import InputField from 'components/common/input-field';
import Select, { IDefaultOption } from 'components/common/select';
import { FC, useState } from 'react';
import { IDMSVehicleDetails, IDMSVehicleDetailsError } from '.';
import InputSearch from 'components/common/input-search';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import moment from 'moment';
import { useAppSelector } from 'hooks/useAppDispatchSelector';
import CheckBox from 'components/common/checkbox';
import { IDevice } from 'interfaces/vehicle-config-interfaces';
import { t } from 'i18next';

const VehicleDetails: FC<{
  vehicle: IDMSVehicleDetails;
  setVehicle: (data: IDMSVehicleDetails) => void;
  vinNumber: string;
  setVinNumber: (data: string) => void;
  handleSearch: () => void;
  tireSizeOption: IDefaultOption[];
  engineOption: IDefaultOption[];
  deviceProviderOption: IDefaultOption[];
  companyOption: IDefaultOption[];
  selectedDevice: IDevice | null;
  setSelectedDevice: (data: IDevice | null) => void;
  vehicleError: IDMSVehicleDetailsError | null;
}> = ({
  vehicle,
  setVehicle,
  vinNumber,
  setVinNumber,
  handleSearch,
  tireSizeOption,
  engineOption,
  deviceProviderOption,
  companyOption,
  selectedDevice,
  setSelectedDevice,
  vehicleError,
}) => {
  const {
    vehicleCategoryList,
    vehicleTransmissionList,
    vehicleStatusList,
    vehicleFuelTypeList,
    vehicleEnvLableList,
  } = useAppSelector(state => state.vehicles);
  const { organizationAssociateParking } = useAppSelector(state => state.adminRole);
  const [dropdownSearch, serDropdownSearch] = useState<string>('');

  const getParkingList = () => {
    try {
      const selectedOrganization = organizationAssociateParking.find(
        data => data._id === vehicle?.organization?.id,
      );
      return selectedOrganization?.parking
        ? [...selectedOrganization?.parking.filter(item => !item?.is_deleted)]
        : [];
    } catch (error) {
      return [];
    }
  };

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto items-center min-h-[570px] gap-y-4 px-3">
      <div className="flex items-center w-full gap-x-4">
        <div className="flex items-center w-3/4">
          <InputSearch
            placeholder={t('vin_number')}
            value={vinNumber}
            onChange={val => setVinNumber(val)}
          />
        </div>
        <PrimaryDarkButton label={t('SEARCH')} onClick={handleSearch} width="w-1/4" />
      </div>
      {vehicle?.registration_date ? (
        <div className="flex flex-wrap flex-1">
          <div className="flex flex-wrap w-full bg-grey4 rounded-4 p-4 mb-4">
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('REG_DATE')}</label>
              <label className="font-medium text-base text-grey9">
                {vehicle?.registration_date
                  ? moment(vehicle?.registration_date).format('MMMM DD, YYYY')
                  : '-'}
              </label>
            </div>
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('purchase_date')}</label>
              <label className="font-medium text-base text-grey9">
                {vehicle?.purchase_date
                  ? moment(vehicle?.purchase_date).format('MMMM DD, YYYY')
                  : '-'}
              </label>
            </div>
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('BRAND')}</label>
              <label className="font-medium text-base text-grey9">
                {vehicle?.brand?.name || '-'}
              </label>
            </div>
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('MODEL')}</label>
              <label className="font-medium text-base text-grey9">{vehicle?.model || '-'}</label>
            </div>
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('ASSIGNED_USER')}</label>
              <label className="font-medium text-base text-grey9">
                {vehicle?.assigned_user || '-'}
              </label>
            </div>
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('VIN')}</label>
              <label className="font-medium text-base text-grey9">{vehicle?.vin || '-'}</label>
            </div>
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('KM')}</label>
              <label className="font-medium text-base text-grey9">{`${vehicle?.km} KM`}</label>
            </div>
            <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">{t('new_used_car')}</label>
              <label className="font-medium text-base text-grey9">
                {vehicle.is_used ? 'Used Car' : 'New Car' || '-'}
              </label>
            </div>
            {/* <div className="flex flex-wrap w-1/4 mb-2">
              <label className="font-medium text-sm text-black2 w-full">Version</label>
              <label className="font-medium text-base text-grey9">{vehicle?.version || '-'}</label>
            </div> */}
          </div>
          <div className="w-full px-3">
            <label className="font-bold text-base text-black2">{t('ORG_DETAILS')}</label>
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">
              {t('ORG')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              options={organizationAssociateParking || []}
              selected={vehicle?.organization || null}
              setSelected={val => setVehicle({ ...vehicle, organization: { ...val } })}
              placeholder="Please select organization"
              error={vehicleError?.organization || ''}
              isSearchable
              search={dropdownSearch}
              setSearch={val => serDropdownSearch(val)}
              removeSearchOnBlur={true}
            />
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">
              {t('administrator_roles.addEdit.associateCompany')}
            </label>
            <Select
              placeholder={t('PLEASE_SEL_ASS_COM')}
              selected={vehicle?.associtedCompany || null}
              setSelected={val => setVehicle({ ...vehicle, associtedCompany: val })}
              options={companyOption}
              isSearchable
              search={''}
              setSearch={val => {}}
              removeSearchOnBlur={true}
              showCross
              handleCross={() => {}}
            />
          </div>
          <div className="w-full px-3">
            <div className="flex flex-wrap rounded-2 mt-3 bg-bg-50 p-4 mb-3 border-1.5">
              {getParkingList().length > 0 ? (
                getParkingList().map((parking_add, index) => (
                  <div className="flex flex-col w-1/3 p-3" key={parking_add?._id}>
                    <div className="relative ps-6" key={parking_add?._id}>
                      <div className="absolute left-0 top-[-3px] mt-[6px]">
                        <CheckBox
                          checked={parking_add?._id === vehicle?.selectedParkingAddress?._id}
                          onChange={checked =>
                            setVehicle({
                              ...vehicle,
                              selectedParkingAddress: checked
                                ? {
                                    address1: parking_add?.address1 || '',
                                    address2: parking_add?.address2 || '',
                                    city: parking_add?.city,
                                    state: parking_add?.state,
                                    country: parking_add?.country,
                                    zipcode: parking_add?.zipcode,
                                    _id: parking_add?._id || '',
                                  }
                                : null,
                            })
                          }
                        />
                      </div>
                      <label className="block text-base font-bold w-full">
                        {parking_add?.address_title || `Address ${index + 1}`}
                      </label>
                      <label className="text-sm text-black2 w-full flex flex-wrap break-words">
                        {`${parking_add?.address1}, ${
                          parking_add?.address2 ? `${parking_add?.address2},` : ''
                        } ${parking_add?.city}, ${parking_add?.state}, ${parking_add?.country} - ${parking_add?.zipcode}.`}
                      </label>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center py-4 px-1 w-full rounded-2 bg-bg-50">
                  <label className="text-base font-medium text-grey3 text-center w-full mx-auto">
                    {t('EMPTY_PARK_ADD_MSG')}
                  </label>
                </div>
              )}
            </div>
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">
              {t('workshopAppointment.deviceProvider')}
            </label>
            <Select
              placeholder={t('SEL_DEVICE_PROVIDER')}
              selected={vehicle?.provider || null}
              setSelected={val => {
                setVehicle({ ...vehicle, provider: val });
              }}
              options={deviceProviderOption}
              error={vehicleError?.device_provider}
            />
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">{t('Device_IMEI')}</label>
            <InputField
              placeholder={t('ENTER_DEVICE_IMEI')}
              value={vehicle?.device || ''}
              onChange={val => setVehicle({ ...vehicle, device: val })}
              error={vehicleError?.device}
              disabled={!vehicle?.provider}
            />
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">
              {t('FRONT_TIRE_SIZE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('SEL_TIRE_SIZE')}
              selected={vehicle?.front_tire_size || null}
              setSelected={val => setVehicle({ ...vehicle, front_tire_size: val })}
              options={tireSizeOption}
              error={vehicleError?.front_tire_size || ''}
            />
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">
              {t('REAR_TIRE_SIZE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('SEL_REAR_TIRE_SIZE')}
              selected={vehicle?.rear_tire_size || null}
              setSelected={val => setVehicle({ ...vehicle, rear_tire_size: val })}
              options={tireSizeOption}
              error={vehicleError?.rear_tire_size || ''}
            />
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">{t('CAT_ORG')}</label>
            <Select
              placeholder={t('SEL_CAT_ORG')}
              selected={vehicle?.category || null}
              setSelected={val => setVehicle({ ...vehicle, category: val })}
              options={vehicleCategoryList}
            />
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">
              {t('ENGINE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('SEL_ENGINE')}
              selected={vehicle?.engine}
              setSelected={val => setVehicle({ ...vehicle, engine: val })}
              options={engineOption}
              error={vehicleError?.engine || ''}
            />
          </div>
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">
              {t('PLATE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('ENTER_PLATE')}
              value={vehicle?.plate}
              onChange={val => setVehicle({ ...vehicle, plate: val })}
              error={vehicleError?.plate || ''}
            />
          </div>
          {/* <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">{t('TUTION')}</label>
            <InputField
              placeholder={t('ENTER_TUTION')}
              value={vehicle?.tutaion || ''}
              onChange={val => setVehicle({ ...vehicle, tutaion: val })}
            />
          </div>
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">{t('FRAME')}</label>
            <InputField
              placeholder={t('ENTER_FRAME')}
              value={vehicle?.frame || ''}
              onChange={val => setVehicle({ ...vehicle, frame: val })}
            />
          </div> */}
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">
              {t('TRANSMISSION')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('TRANSMISSION')}
              selected={vehicle?.transmission}
              setSelected={val => setVehicle({ ...vehicle, transmission: val })}
              options={vehicleTransmissionList}
              error={vehicleError?.trasmission || ''}
              dropdownPlacement="TOP"
            />
          </div>
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">{t('DOORS')}</label>
            <InputField
              placeholder={t('ENTER_DOORS')}
              value={vehicle?.doors || ''}
              onChange={val => setVehicle({ ...vehicle, doors: val })}
            />
          </div>
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">{t('PLACES')}</label>
            <InputField
              placeholder={t('ENTER_PLACES')}
              value={vehicle?.places || ''}
              onChange={val => setVehicle({ ...vehicle, places: val })}
            />
          </div>
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">
              {t('ENV_LABLE')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('ENV_LABLE')}
              selected={vehicle?.environmental_label}
              setSelected={val => setVehicle({ ...vehicle, environmental_label: val })}
              options={vehicleEnvLableList}
              error={vehicleError?.env_label || ''}
              dropdownPlacement="TOP"
            />
          </div>
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">
              {t('STATUS')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <Select
              placeholder={t('STATUS')}
              selected={vehicle?.status || null}
              setSelected={val => setVehicle({ ...vehicle, status: val })}
              options={vehicleStatusList}
              error={vehicleError?.status || ''}
              dropdownPlacement="TOP"
            />
          </div>
          <div className="mb-3 px-3 w-1/4">
            <label className="font-medium text-sm text-black w-full">{t('FUEL_TYPE')}</label>
            <Select
              placeholder={t('campaigns.placeholder_tire_size')}
              selected={vehicle?.fuel_type || null}
              setSelected={val => setVehicle({ ...vehicle, fuel_type: val })}
              options={vehicleFuelTypeList}
              dropdownPlacement="TOP"
            />
          </div>
          <div className="mb-3 px-3 w-1/2">
            <label className="font-medium text-sm text-black w-full">{t('VERSION')}</label>
            <InputField
              placeholder={t('VERSION')}
              value={vehicle?.version || ''}
              onChange={val => setVehicle({ ...vehicle, version: val })}
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center bg-grey4 flex-1 w-full rounded-4">
          <label className="text-xl font-semibold w-full text-center">
            {t('dms_Integartion.errorNoVehicleMessage')}
          </label>
        </div>
      )}
    </div>
  );
};

export default VehicleDetails;
